export type MarginDecisionInput = Readonly<{
  salesPrice: number;
  cost: number;
  platformFeeRate?: number;
  paymentFeeRate?: number;
  shippingCost?: number;
  packagingCost?: number;
  adCost?: number;
  otherCost?: number;
}>;

export type MarginDecisionResult = Readonly<{
  salesPrice: number;
  productCost: number;
  feeRate: number;
  feeAmount: number;
  fixedOrderCosts: number;
  totalCost: number;
  profit: number;
  marginRate: number;
  costRate: number;
}>;

export type MarginScenario = Readonly<{
  id: 'current' | 'discount-10' | 'cost-up-10';
  label: string;
  description: string;
  result: MarginDecisionResult;
}>;

function optionalAmount(value: number | undefined): number {
  return value ?? 0;
}

function roundWon(value: number): number {
  return Math.round(value);
}

function assertFiniteNonNegative(label: string, value: number): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${label} must be a finite non-negative number.`);
  }
}

export function calculateMarginDecision(input: MarginDecisionInput): MarginDecisionResult {
  if (!Number.isFinite(input.salesPrice) || input.salesPrice <= 0) {
    throw new RangeError('salesPrice must be a finite number greater than zero.');
  }

  const platformFeeRate = optionalAmount(input.platformFeeRate);
  const paymentFeeRate = optionalAmount(input.paymentFeeRate);
  const shippingCost = optionalAmount(input.shippingCost);
  const packagingCost = optionalAmount(input.packagingCost);
  const adCost = optionalAmount(input.adCost);
  const otherCost = optionalAmount(input.otherCost);

  assertFiniteNonNegative('cost', input.cost);
  assertFiniteNonNegative('platformFeeRate', platformFeeRate);
  assertFiniteNonNegative('paymentFeeRate', paymentFeeRate);
  assertFiniteNonNegative('shippingCost', shippingCost);
  assertFiniteNonNegative('packagingCost', packagingCost);
  assertFiniteNonNegative('adCost', adCost);
  assertFiniteNonNegative('otherCost', otherCost);

  const feeRate = platformFeeRate + paymentFeeRate;
  const feeAmount = input.salesPrice * (feeRate / 100);
  const fixedOrderCosts = input.cost + shippingCost + packagingCost + adCost + otherCost;
  const totalCost = fixedOrderCosts + feeAmount;
  const profit = input.salesPrice - totalCost;

  return {
    salesPrice: roundWon(input.salesPrice),
    productCost: roundWon(input.cost),
    feeRate,
    feeAmount: roundWon(feeAmount),
    fixedOrderCosts: roundWon(fixedOrderCosts),
    totalCost: roundWon(totalCost),
    profit: roundWon(profit),
    marginRate: (profit / input.salesPrice) * 100,
    costRate: (totalCost / input.salesPrice) * 100,
  };
}

export function calculateTargetMarginPrice(
  input: Omit<MarginDecisionInput, 'salesPrice'>,
  targetMarginRate: number,
): number | null {
  if (!Number.isFinite(targetMarginRate) || targetMarginRate < 0 || targetMarginRate >= 100) {
    return null;
  }

  const platformFeeRate = optionalAmount(input.platformFeeRate);
  const paymentFeeRate = optionalAmount(input.paymentFeeRate);
  const feeRate = (platformFeeRate + paymentFeeRate) / 100;
  const targetRate = targetMarginRate / 100;
  const denominator = 1 - feeRate - targetRate;

  if (denominator <= 0) return null;

  const fixedOrderCosts = input.cost
    + optionalAmount(input.shippingCost)
    + optionalAmount(input.packagingCost)
    + optionalAmount(input.adCost)
    + optionalAmount(input.otherCost);

  return Math.ceil(fixedOrderCosts / denominator);
}

export function buildMarginScenarios(input: MarginDecisionInput): readonly MarginScenario[] {
  return [
    {
      id: 'current',
      label: '현재 조건',
      description: '입력한 판매가와 비용을 그대로 적용합니다.',
      result: calculateMarginDecision(input),
    },
    {
      id: 'discount-10',
      label: '10% 할인',
      description: '비용은 유지하고 판매가만 10% 낮춘 경우입니다.',
      result: calculateMarginDecision({ ...input, salesPrice: input.salesPrice * 0.9 }),
    },
    {
      id: 'cost-up-10',
      label: '원가 10% 상승',
      description: '판매가는 유지하고 상품 원가만 10% 오른 경우입니다.',
      result: calculateMarginDecision({ ...input, cost: input.cost * 1.1 }),
    },
  ];
}

export function describeMarginDecision(result: MarginDecisionResult): string {
  const absoluteProfit = Math.abs(result.profit).toLocaleString('ko-KR');
  const margin = result.marginRate.toLocaleString('ko-KR', { maximumFractionDigits: 1 });

  if (result.profit < 0) {
    return `현재 입력 조건에서는 주문 1건당 약 ${absoluteProfit}원 손실이며, 마진율은 ${margin}%입니다. 할인·수수료·원가 조건을 조정해야 합니다.`;
  }

  if (result.profit === 0) {
    return '현재 입력 조건에서는 주문 1건당 남는 이익이 0원입니다. 추가 비용이 발생하면 손실로 전환될 수 있습니다.';
  }

  return `현재 입력한 주문당 비용을 반영하면 1건당 약 ${absoluteProfit}원이 남고, 판매가의 ${margin}%가 이익으로 계산됩니다.`;
}
